import { DateTime } from "luxon";
import type { Path } from "@/router";
import type {
  AssociateDashboardCelebration,
  AssociateDashboardCelebrationKey,
  AssociateDashboardPacingLevel,
  AssociateDashboardSignal,
  AssociateDashboardSignalKey
} from "../queries";
import type { DashboardMemberSignalGroup } from "./dashboardSignal";
import type { DashboardPacingRow } from "./dashboardPacing";

const ASSOCIATE_SIGNAL_KEYS: AssociateDashboardSignalKey[] = [
  "LEVEL_1_RENEWALS",
  "LEVEL_2_RENEWALS",
  "LEVEL_3_RENEWALS",
  "SUSPENDED_ASSOCIATES"
];

const ASSOCIATE_SIGNAL_GROUP_CONFIG: Record<AssociateDashboardSignalKey, { id: string; label: string }> = {
  LEVEL_1_RENEWALS: { id: "level-1-renewals", label: "LEVEL 1 RENEWALS" },
  LEVEL_2_RENEWALS: { id: "level-2-renewals", label: "LEVEL 2 RENEWALS" },
  LEVEL_3_RENEWALS: { id: "level-3-renewals", label: "LEVEL 3 RENEWALS" },
  SUSPENDED_ASSOCIATES: { id: "suspended-associates", label: "SUSPENDED ASSOCIATES" }
};

const ASSOCIATE_CELEBRATION_KEYS: AssociateDashboardCelebrationKey[] = ["PAT_PASSED", "LEVEL_COMPLETIONS", "BIRTHDAYS"];

const ASSOCIATE_CELEBRATION_GROUP_CONFIG: Record<AssociateDashboardCelebrationKey, { id: string; label: string }> = {
  PAT_PASSED: { id: "pat-passed", label: "PAT PASSED" },
  LEVEL_COMPLETIONS: { id: "level-completions", label: "LEVEL COMPLETIONS" },
  BIRTHDAYS: { id: "associate-birthdays", label: "BIRTHDAYS" }
};

const ASSOCIATE_SIGNAL_KIND_LABELS: Record<string, string> = {
  RENEWAL_DUE: "Renewal due",
  SUSPENDED: "Suspended"
};

function getInitials(displayName: string): string {
  const initials = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  return initials || "AS";
}

function formatRelativeDate(date: string): string {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return "recently";
  }

  const daysFromToday = Math.round(parsedDate.startOf("day").diff(DateTime.now().startOf("day"), "days").days);
  if (daysFromToday === 0) {
    return "today";
  }

  if (daysFromToday > 0) {
    return `in ${daysFromToday} day${daysFromToday === 1 ? "" : "s"}`;
  }

  const daysAgo = Math.abs(daysFromToday);
  return `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
}

function formatCelebrationDate(date: string): string {
  const parsedDate = DateTime.fromISO(date);
  return parsedDate.isValid ? parsedDate.toLocaleString(DateTime.DATE_MED) : "recently";
}

function toAssociateSignal(row: AssociateDashboardSignal["rows"][number]) {
  const role = row.title ?? (row.associateLevel ? `Associate Level ${row.associateLevel}` : undefined);
  const detail = row.detail?.trim() || ASSOCIATE_SIGNAL_KIND_LABELS[row.attentionKind] || "Attention needed";

  return {
    id: row.id,
    pgaId: row.pgaId,
    name: row.displayName,
    initials: getInitials(row.displayName),
    role,
    facility: row.facilityName ?? undefined,
    activityLabel: `${detail} ${formatRelativeDate(row.signalDate)}`,
    href: row.pgaId ? (`/people/${row.pgaId}/person/overview` as Path) : null
  };
}

export function toDashboardAssociateSignalGroups(signals: AssociateDashboardSignal[]): DashboardMemberSignalGroup[] {
  const signalsByKey = new Map(signals.map((signal) => [signal.key, signal]));

  return ASSOCIATE_SIGNAL_KEYS.map((signalKey) => {
    const config = ASSOCIATE_SIGNAL_GROUP_CONFIG[signalKey];
    const signal = signalsByKey.get(signalKey);

    return {
      id: config.id,
      label: config.label,
      count: signal?.totalCount ?? 0,
      hasMore: signal?.hasMore ?? false,
      rows: signal?.rows.map(toAssociateSignal) ?? []
    };
  });
}

function toAssociateCelebration(row: AssociateDashboardCelebration["rows"][number]) {
  return {
    id: row.id,
    pgaId: row.pgaId,
    name: row.displayName,
    initials: getInitials(row.displayName),
    role: row.title ?? undefined,
    facility: row.facilityName ?? undefined,
    activityLabel: `${row.detail?.trim() || "Celebration"} · ${formatCelebrationDate(row.celebrationDate)}`,
    href: row.pgaId ? (`/people/${row.pgaId}/person/overview` as Path) : null
  };
}

export function toDashboardAssociateCelebrationGroups(
  celebrations: AssociateDashboardCelebration[]
): DashboardMemberSignalGroup[] {
  const celebrationsByKey = new Map(celebrations.map((celebration) => [celebration.key, celebration]));

  return ASSOCIATE_CELEBRATION_KEYS.map((celebrationKey) => {
    const config = ASSOCIATE_CELEBRATION_GROUP_CONFIG[celebrationKey];
    const celebration = celebrationsByKey.get(celebrationKey);

    return {
      id: config.id,
      label: config.label,
      count: celebration?.totalCount ?? 0,
      hasMore: celebration?.hasMore ?? false,
      rows: celebration?.rows.map(toAssociateCelebration) ?? []
    };
  });
}

export function toDashboardAssociatePacingRows(levels: AssociateDashboardPacingLevel[]): DashboardPacingRow[] {
  return levels.map((level) => ({
    id: `associate-${level.key.toLowerCase().replace(/_/g, "-")}`,
    label: level.label,
    percent: level.percent,
    completed: level.completed,
    total: level.total,
    detailLabel: "completed"
  }));
}
